import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  SimpleGrid,
  Stack,
  Switch,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { createShipTo, deleteShipTo, updateShipTo, type ShipToDto } from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Null creates a new entry. */
  shipTo: ShipToDto | null;
  onSaved: () => void;
}

export function ShipToFormModal({ opened, onClose, shipTo, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = shipTo !== null;

  const [shipToName, setShipToName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [phone, setPhone] = useState('');
  const [fax, setFax] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setSubmitError(null);
    setShipToName(shipTo?.shipToName ?? '');
    setAddressLine1(shipTo?.addressLine1 ?? '');
    setAddressLine2(shipTo?.addressLine2 ?? '');
    setCity(shipTo?.city ?? '');
    setStateProvince(shipTo?.stateProvince ?? '');
    setPostalCode(shipTo?.postalCode ?? '');
    setPhone(shipTo?.phone ?? '');
    setFax(shipTo?.fax ?? '');
    setIsActive(shipTo?.isActive ?? true);
  }, [opened, shipTo]);

  const body = () => ({
    shipToName: shipToName.trim(),
    addressLine1: addressLine1.trim() || null,
    addressLine2: addressLine2.trim() || null,
    city: city.trim() || null,
    stateProvince: stateProvince.trim() || null,
    postalCode: postalCode.trim() || null,
    phone: phone.trim() || null,
    fax: fax.trim() || null,
    isActive,
  });

  const save = useMutation({
    mutationFn: () =>
      isEdit ? updateShipTo(shipTo!.purchaseOrderShipToId, body()) : createShipTo(body()),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.problem.type?.includes('duplicate_ship_to_name')) {
        setErrors({ shipToName: t('shipTos.errors.duplicateName') });
        return;
      }
      setSubmitError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title ?? t('shipTos.errors.saveFailed'))
          : t('shipTos.errors.saveFailed'),
      );
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteShipTo(shipTo!.purchaseOrderShipToId),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    // The server names the entry and says to deactivate instead, which is the useful text.
    onError: (err) =>
      setSubmitError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title ?? t('shipTos.errors.deleteFailed'))
          : t('shipTos.errors.deleteFailed'),
      ),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!shipToName.trim()) {
      setErrors({ shipToName: t('shipTos.errors.nameRequired') });
      return;
    }
    setErrors({});
    save.mutate();
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('shipTos.edit', { name: shipTo!.shipToName }) : t('shipTos.new')}
      width="md"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <TextInput
            label={t('shipTos.name')}
            description={t('shipTos.nameHint')}
            value={shipToName}
            onChange={(e) => setShipToName(e.currentTarget.value)}
            error={errors.shipToName}
            maxLength={200}
            required
          />

          <TextInput
            label={t('shipTos.addressLine1')}
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.currentTarget.value)}
            maxLength={255}
          />
          <TextInput
            label={t('shipTos.addressLine2')}
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.currentTarget.value)}
            maxLength={255}
          />

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <TextInput
              label={t('shipTos.city')}
              value={city}
              onChange={(e) => setCity(e.currentTarget.value)}
              maxLength={100}
            />
            <TextInput
              label={t('shipTos.state')}
              value={stateProvince}
              onChange={(e) => setStateProvince(e.currentTarget.value)}
              maxLength={10}
            />
            <TextInput
              label={t('shipTos.postalCode')}
              value={postalCode}
              onChange={(e) => setPostalCode(e.currentTarget.value)}
              maxLength={20}
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t('shipTos.phone')}
              value={phone}
              onChange={(e) => setPhone(e.currentTarget.value)}
              maxLength={50}
            />
            <TextInput
              label={t('shipTos.fax')}
              value={fax}
              onChange={(e) => setFax(e.currentTarget.value)}
              maxLength={50}
            />
          </SimpleGrid>

          <Switch
            label={t('shipTos.active')}
            description={t('shipTos.activeHint')}
            checked={isActive}
            onChange={(e) => setIsActive(e.currentTarget.checked)}
          />

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group justify="space-between">
            {/* Only offered when it is possible. An entry on an order is refused by the server
                (53713) and the button would exist purely to be told no. */}
            <div>
              {isEdit && !shipTo!.isInUse && (
                <Button
                  variant="subtle"
                  color="red"
                  onClick={() => remove.mutate()}
                  loading={remove.isPending}
                >
                  {t('shipTos.delete')}
                </Button>
              )}
            </div>
            <Group gap="sm">
              <Button variant="default" onClick={onClose}>
                {t('shell.cancel')}
              </Button>
              <Button type="submit" color="invetrak" loading={save.isPending}>
                {t('shell.saveChanges')}
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
